/**
 * File System Access API Utilities
 *
 * Functions for saving tests directly to the file system using the
 * File System Access API (available in modern browsers).
 */

import { encryptTestCode } from "./encryption";
import type { GeneratedTest } from "./types";
import { getStateIdFromUrl } from "./utils";

// ============================================================================
// File System Access API
// ============================================================================

/**
 * Check if File System Access API is supported
 */
export function isFileSystemAccessSupported(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

/**
 * Save encrypted test directly to a directory using File System Access API
 * Uses stateId from URL as filename for correlation with kiosk session
 * Returns the filename if successful, null if failed
 */
export async function saveTestToDirectory(
  test: GeneratedTest,
  directoryHandle: FileSystemDirectoryHandle,
): Promise<string | null> {
  try {
    // Use stateId from URL as filename (correlates with localStorage kiosk-state:{stateId})
    const stateId = getStateIdFromUrl();
    const filename = stateId
      ? `${stateId}.enc.txt`
      : `trace-${test.metadata.traceId.replace(/[^a-z0-9-]/gi, "-")}.enc.txt`;

    // Encrypt the test code
    const encrypted = encryptTestCode(test.testCode);

    // Create or overwrite the file
    const fileHandle = await directoryHandle.getFileHandle(filename, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(encrypted);
    await writable.close();

    console.log(`[playwright-generator] Saved encrypted test to: ${filename}`);
    return filename;
  } catch (error) {
    console.error("[playwright-generator] Failed to save to directory:", error);
    return null;
  }
}
